import React, { FC, useCallback, useMemo } from 'react';
import UploadWithSampleModal from '../flatfile/UploadWithSampleModal';
import {
	getFlatfileRecordForPart,
	PART_MASTER_FLATFILE_HEADER,
	PARTIAL_UPLOAD_CRUD_FIELD,
	PARTS_MASTER_LIST_PARTIAL_FLATFILE_HEADER,
} from '../stock_locations_detail_details_page/stock_location_constants';
import { getCurrency } from '../../utils/DataAccessUtils';
import { CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';
import { connect } from 'react-redux';
import { ExportToCsv } from 'export-to-csv';
import { requiredWithValueRecordHook } from '../flatfile/faltfile_utils';
import { isGLCodeAllowedBySettings } from '../../utils/InventoryUtils';
import { LabelOption } from '../../types/labels';

const PARTIAL_SAMPLE_LABELS = ['Example Label 1', 'Example Label 2'];

const PartialPartsUpload: FC<any> = ({
	hideUploadPopup,
	vendorOptions,
	onDataUpload,
	currentUser,
	companyConfig,
	labelOptions,
}): React.ReactElement => {
	const allLabelOptions: LabelOption[] = useMemo(() => labelOptions || [], [labelOptions]);

	const userCurrency = useMemo(() => getCurrency({ currentUser }).id, [currentUser]);

	const glCodeEnabled = useMemo(() => isGLCodeAllowedBySettings(companyConfig), [companyConfig]);

	const headerDefs = useMemo(
		() => [
			PARTIAL_UPLOAD_CRUD_FIELD,
			...PART_MASTER_FLATFILE_HEADER([userCurrency], vendorOptions, glCodeEnabled, allLabelOptions),
		],
		[userCurrency, vendorOptions, glCodeEnabled, allLabelOptions]
	);

	const onDownloadSample = useCallback(() => {
		const csvTitle = `Partial_Parts_Upload_Sample`;

		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: csvTitle,
			title: csvTitle,
			headers: headerDefs.map((h) => h.label),
		});

		const data = [
			{
				...getFlatfileRecordForPart(
					{ partLabels: PARTIAL_SAMPLE_LABELS },
					userCurrency,
					headerDefs
				),
				crudAction: 'create',
			},
			{
				...getFlatfileRecordForPart(
					{ id: 1234, partLabels: PARTIAL_SAMPLE_LABELS },
					userCurrency,
					headerDefs
				),
				crudAction: 'update',
			},
			{
				...getFlatfileRecordForPart(
					{ id: 1234, partLabels: PARTIAL_SAMPLE_LABELS },
					userCurrency,
					headerDefs
				),
				crudAction: 'delete',
			},
		];
		csvExporter.generateCsv(data);
	}, [headerDefs, userCurrency]);

	const recordHook = useCallback(
		(record) => {
			let result = requiredWithValueRecordHook(['id'], 'crudAction', ['update', 'delete'])(record);

			const labelsValue = result.get('labels');
			if (labelsValue) {
				let rawLabels: string[] = [];
				if (typeof labelsValue === 'string' && labelsValue.trim()) {
					rawLabels = labelsValue
						.split(',')
						.map((s) => s.trim())
						.filter(Boolean);
				} else if (Array.isArray(labelsValue)) {
					rawLabels = labelsValue
						.flatMap((v) => String(v).split(','))
						.map((s) => s.trim())
						.filter(Boolean);
				}

				if (rawLabels.length > 0) {
					const validValues: string[] = [];
					const invalidLabelNames: string[] = [];
					rawLabels.forEach((labelOrId) => {
						const match = allLabelOptions.find(
							(opt) => opt.label === labelOrId || opt.value === labelOrId
						);
						if (match) {
							validValues.push(match.value);
						} else {
							invalidLabelNames.push(labelOrId);
						}
					});

					result.set('labels', validValues);
					if (invalidLabelNames.length > 0) {
						result.addError(
							'labels',
							`Unknown labels: ${invalidLabelNames.join(
								', '
							)}. Please select from existing labels only.`
						);
					}
				}
			}

			return result;
		},
		[allLabelOptions]
	);

	return (
		<UploadWithSampleModal
			type="Partial Parts Upload"
			title="Partial Parts Upload"
			downloadText="Do you want to download Sample?"
			sampleFileText="Download Sample"
			recordHook={recordHook}
			onCancel={hideUploadPopup}
			onDownloadSample={onDownloadSample}
			fields={PARTS_MASTER_LIST_PARTIAL_FLATFILE_HEADER(
				[userCurrency],
				vendorOptions,
				glCodeEnabled,
				allLabelOptions
			)}
			onDataLoad={onDataUpload}
		/>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

export default connect(mapStateToProps)(PartialPartsUpload as any);
